import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LOUNGE_NAME } from '../utils/branding';

const API = process.env.REACT_APP_API_URL || 'https://lacasadelpuro-pos-production.up.railway.app';

const GOLD = '#C9A84C';
const GOLD_DIM = 'rgba(201,168,76,0.15)';
const GOLD_BORDER = 'rgba(201,168,76,0.3)';
const BG = '#0D0D0D';
const CARD = '#1a1a1a';
const INNER = '#222222';
const BORDER = '#2a2a2a';
const TEXT = '#F0F0F0';
const MUTED = '#888888';

const TABS = ['Overview', 'Campaigns', 'Campaign builder', 'Social content', 'Outreach center', 'Content calendar'];

const BADGES = {
  SENT:      { bg: '#0D2010', color: '#97C459' },
  COMPLETE:  { bg: '#0D2010', color: '#97C459' },
  ACTIVE:    { bg: '#1E1A3D', color: '#AFA9EC' },
  AI_READY:  { bg: '#1E1A3D', color: '#AFA9EC' },
  REVIEW:    { bg: '#3D1A00', color: '#EF9F27' },
  SCHEDULED: { bg: '#0A1A2E', color: '#85B7EB' },
  UPCOMING:  { bg: '#0A1A2E', color: '#85B7EB' },
  PLANNED:   { bg: '#0A1A2E', color: '#85B7EB' },
  AUTO:      { bg: '#0D2A22', color: '#5DCAA5' },
  EMAIL:     { bg: '#0A1A2E', color: '#85B7EB' },
  SMS:       { bg: '#0D2A22', color: '#5DCAA5' },
  BOTH:      { bg: '#1E1A3D', color: '#AFA9EC' },
  QUEUED:    { bg: '#222222', color: '#AAAAAA' },
  FAILED:    { bg: '#2D0A0A', color: '#E84545' },
};

const styles = {
  card: { background: CARD, border: '1px solid ' + BORDER, borderRadius: 12, padding: 18 },
  cardTitle: { fontSize: 11, fontWeight: 600, letterSpacing: 2, color: GOLD, marginBottom: 14, textTransform: 'uppercase' },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 16 },
  kpi: { background: CARD, border: '1px solid ' + BORDER, borderRadius: 12, padding: 14 },
  kpiLabel: { fontSize: 10, fontWeight: 600, letterSpacing: 1.5, color: MUTED, textTransform: 'uppercase' },
  kpiValue: { fontSize: 24, fontWeight: 700, color: TEXT, marginTop: 6 },
  kpiSub: { fontSize: 11, color: MUTED, marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid ' + BORDER, color: MUTED, fontWeight: 600, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' },
  td: { padding: '10px', borderBottom: '1px solid #1f1f1f', color: TEXT, fontSize: 12, verticalAlign: 'top' },
  field: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: MUTED, letterSpacing: 1, textTransform: 'uppercase' },
  input: { background: INNER, border: '1px solid ' + BORDER, borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, fontFamily: 'inherit' },
  select: { background: INNER, border: '1px solid ' + BORDER, borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, fontFamily: 'inherit' },
  textarea: { background: INNER, border: '1px solid ' + BORDER, borderRadius: 6, padding: '10px', color: TEXT, fontSize: 13, fontFamily: 'inherit', minHeight: 90, resize: 'vertical' },
  chip: (active) => ({
    fontSize: 11, padding: '6px 12px', borderRadius: 16, border: '1px solid ' + (active ? GOLD_BORDER : BORDER),
    background: active ? GOLD_DIM : 'transparent', color: active ? GOLD : MUTED, cursor: 'pointer', fontWeight: active ? 600 : 400,
  }),
  primaryBtn: {
    background: GOLD, color: '#0D0D0D', border: 'none', borderRadius: 6,
    padding: '10px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer',
    textTransform: 'uppercase',
  },
  primaryBtnFull: {
    background: GOLD, color: '#0D0D0D', border: 'none', borderRadius: 6,
    padding: '12px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer',
    textTransform: 'uppercase', width: '100%',
  },
  ghostBtn: {
    background: 'transparent', color: TEXT, border: '1px solid ' + BORDER, borderRadius: 6,
    padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  disabledBtn: {
    background: '#1a1a1a', color: '#555', border: '1px solid ' + BORDER, borderRadius: 6,
    padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'not-allowed',
  },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 },
  emptyState: { padding: '40px 20px', textAlign: 'center', color: MUTED, fontSize: 13 },
  errorBox: { background: '#2D0A0A', color: '#F0B0B0', border: '1px solid #4A1A1A', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginTop: 8 },
  successBox: { background: '#0D2010', color: '#97C459', border: '1px solid #1f3a23', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginTop: 8 },
};

function Badge({ kind, children }) {
  const b = BADGES[kind] || { bg: INNER, color: MUTED };
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
      padding: '3px 8px', borderRadius: 4,
      background: b.bg, color: b.color,
      whiteSpace: 'nowrap', textTransform: 'uppercase',
    }}>{children}</span>
  );
}

function Dot({ color }) {
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 8, verticalAlign: 'middle' }} />;
}

function daysAgo(dateStr) {
  if (!dateStr) return null;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
}

function formatShortDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch (e) { return ''; }
}

// Backend AI route deployed in T3.6: POST /api/ai/complete with
// { feature: 'marketing_draft', prompt } → { content: string }.
async function callAI(prompt) {
  const res = await fetch(API + '/api/ai/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feature: 'marketing_draft', prompt }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error('AI ' + res.status + (text ? ': ' + text.slice(0, 200) : ''));
  }
  const data = await res.json();
  if (!data || typeof data.content !== 'string') {
    throw new Error('AI response missing content field');
  }
  return data.content.trim();
}

// Splits an AI email draft on the first "Subject:" line.
function parseEmailDraft(raw) {
  if (!raw) return { subject: '', body: '' };
  const m = raw.match(/^\s*Subject:\s*(.+?)\s*\n+([\s\S]*)$/i);
  if (m) return { subject: m[1].trim(), body: m[2].trim() };
  return { subject: '', body: raw.trim() };
}

const ALL_AUDIENCE = 'All subscribers';
const MEMBER_AUDIENCE = 'Members only';
const LEAD_AUDIENCE = 'Leads only (non-members)';
const ACTIVE_AUDIENCE = 'Active subscribers';
const AUDIENCES = [ALL_AUDIENCE, MEMBER_AUDIENCE, LEAD_AUDIENCE, ACTIVE_AUDIENCE];

// Resolves audience to backend filter or client-side fan-out.
// Backend supports memberOnly + status; "Leads only" needs client-side
// filtering (fetch active subscribers, exclude member_id rows).
function audienceToFilter(audience) {
  if (audience === MEMBER_AUDIENCE) return { backend: { status: 'active', memberOnly: true } };
  if (audience === LEAD_AUDIENCE) return { client: 'leads' };
  return { backend: { status: 'active' } };
}

async function sendCampaign({ channel, audience, body, subject }) {
  const resolved = audienceToFilter(audience);
  if (resolved.backend) {
    const path = channel === 'SMS' ? '/marketing/sms/blast' : '/marketing/email/blast';
    const payload = channel === 'SMS'
      ? { body, filter: resolved.backend }
      : { subject, html: body, filter: resolved.backend };
    const res = await fetch(API + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || ('HTTP ' + res.status));
    return data;
  }
  // Client-side fan-out for "Leads only"
  const listRes = await fetch(API + '/marketing/subscribers?status=active&limit=200');
  const list = await listRes.json();
  const subs = (list.subscribers || []).filter(s => !s.member_id);
  let sent = 0; let failed = 0;
  for (const s of subs) {
    try {
      const recipient = channel === 'SMS' ? s.phone : s.email;
      if (!recipient) { failed += 1; continue; }
      const path = channel === 'SMS' ? '/marketing/sms/send' : '/marketing/email/send';
      const payload = channel === 'SMS'
        ? { to: recipient, body, subscriberId: s.id }
        : { to: recipient, subject, html: body, subscriberId: s.id };
      const r = await fetch(API + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success) sent += 1; else failed += 1;
    } catch (e) { failed += 1; }
  }
  return { sent, failed, total: subs.length };
}

export default function Marketing({ staff, hideHeader, onBack }) {
  const [activeTab, setActiveTab] = useState('Overview');
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [recentMessages, setRecentMessages] = useState(null);

  const refreshOverview = useCallback(() => {
    fetch(API + '/marketing/subscribers/stats')
      .then(r => r.ok ? r.json() : Promise.reject(new Error('stats ' + r.status)))
      .then(d => { setStats(d); setStatsError(null); })
      .catch(err => setStatsError(err.message));
    fetch(API + '/marketing/messages?limit=10')
      .then(r => r.ok ? r.json() : Promise.reject(new Error('messages ' + r.status)))
      .then(d => setRecentMessages(d))
      .catch(() => setRecentMessages({ messages: [], total: 0 }));
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(API + '/members')
      .then(r => r.json())
      .then(data => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : (data.members || []);
        setMembers(list);
        setLoadingMembers(false);
      })
      .catch(() => { if (alive) setLoadingMembers(false); });
    refreshOverview();
    return () => { alive = false; };
  }, [refreshOverview]);

  const memberCount = members.length;

  const churnRisk = useMemo(() => {
    return members
      .map(m => {
        const last = m.last_visit || m.created_at || m.join_date;
        return { ...m, daysSinceLast: daysAgo(last) };
      })
      .filter(m => m.daysSinceLast === null || m.daysSinceLast > 30)
      .slice(0, 10);
  }, [members]);

  const lowCredit = useMemo(() => {
    return members.filter(m => parseFloat(m.credit_balance || 0) <= 0).slice(0, 10);
  }, [members]);

  function navBtn(tab) {
    return {
      fontSize: 11, padding: '5px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
      whiteSpace: 'nowrap', background: activeTab === tab ? GOLD_DIM : 'transparent',
      color: activeTab === tab ? GOLD : MUTED, fontWeight: activeTab === tab ? 600 : 400,
    };
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif', color: TEXT, background: BG }}>
      {!hideHeader && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid #333', background: CARD }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, color: GOLD }}>PUROAI — MARKETING</div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>AI-powered campaigns, content, and outreach</div>
          </div>
          <span style={{ fontSize: 10, background: GOLD_DIM, color: GOLD, border: '1px solid ' + GOLD_BORDER, padding: '3px 8px', borderRadius: 4 }}>AI-powered</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 2, padding: '6px 18px', borderBottom: '1px solid #333', overflowX: 'auto', background: CARD }}>
        {TABS.map(tab => (
          <button key={tab} style={navBtn(tab)} onClick={() => setActiveTab(tab)}>{tab}</button>
        ))}
      </div>
      <div style={{ padding: '20px 18px', background: BG }}>
        {activeTab === 'Overview' && (
          <OverviewTab
            stats={stats}
            statsError={statsError}
            memberCount={memberCount}
            loadingMembers={loadingMembers}
            recentMessages={recentMessages}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === 'Campaigns' && <CampaignsTab setActiveTab={setActiveTab} />}
        {activeTab === 'Campaign builder' && <BuilderTab onSent={refreshOverview} />}
        {activeTab === 'Social content' && <SocialTab />}
        {activeTab === 'Outreach center' && (
          <OutreachTab
            members={members}
            churnRisk={churnRisk}
            lowCredit={lowCredit}
            loadingMembers={loadingMembers}
            onSent={refreshOverview}
          />
        )}
        {activeTab === 'Content calendar' && <CalendarTab />}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tab 1: Overview
// ────────────────────────────────────────────────────────────────────
function OverviewTab({ stats, statsError, memberCount, loadingMembers, recentMessages, setActiveTab }) {
  const total = stats ? stats.total : null;
  const active = stats ? stats.active : null;
  const messagesSent = recentMessages ? recentMessages.total : null;

  const automations = [
    { name: 'Churn risk outreach', desc: 'Identifies members inactive 30+ days', status: 'ACTIVE', dot: '#AFA9EC' },
    { name: 'Credit expiry reminder', desc: 'Alerts before monthly credit resets', status: 'ACTIVE', dot: '#AFA9EC' },
    { name: 'New arrival match', desc: 'Matches new cigars to member taste profiles', status: 'PLANNED', dot: '#85B7EB' },
    { name: 'Guest conversion', desc: 'Nudges repeat guests toward membership', status: 'PLANNED', dot: '#85B7EB' },
  ];

  const recent = recentMessages && Array.isArray(recentMessages.messages)
    ? recentMessages.messages.slice(0, 5)
    : [];

  return (
    <div>
      {statsError && (
        <div style={{ ...styles.errorBox, marginBottom: 12 }}>
          Live stats unavailable — {statsError}
        </div>
      )}
      <div style={styles.kpiRow}>
        <KpiCard label="Total subscribers" value={total == null ? '—' : total} sub="From /marketing/subscribers/stats" />
        <KpiCard label="Active members" value={loadingMembers ? '—' : memberCount} sub="From /members" />
        <KpiCard label="Active SMS subscribers" value={active == null ? '—' : active} sub="Status = active" />
        <KpiCard label="Messages sent" value={messagesSent == null ? '—' : messagesSent} sub="From message log" />
      </div>

      <div style={{ ...styles.card, marginBottom: 16 }}>
        <div style={styles.cardTitle}>Active AI automations</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 10 }}>
          {automations.map(a => (
            <div key={a.name} style={{ background: INNER, border: '1px solid ' + BORDER, borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>
                  <Dot color={a.dot} />{a.name}
                </div>
                <Badge kind={a.status}>{a.status}</Badge>
              </div>
              <div style={{ fontSize: 11, color: MUTED, marginLeft: 16 }}>{a.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ ...styles.cardTitle, marginBottom: 0 }}>Recent activity</div>
          <button style={styles.ghostBtn} onClick={() => setActiveTab('Campaigns')}>See all →</button>
        </div>
        {recent.length === 0 ? (
          <div style={styles.emptyState}>
            No messages sent yet — build your first campaign below.
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>When</th>
                <th style={styles.th}>Channel</th>
                <th style={styles.th}>Recipient</th>
                <th style={styles.th}>Subject / Preview</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.map(m => (
                <tr key={m.id}>
                  <td style={styles.td}>{formatShortDate(m.created_at)}</td>
                  <td style={styles.td}><Badge kind={m.channel === 'sms' ? 'SMS' : 'EMAIL'}>{m.channel}</Badge></td>
                  <td style={styles.td}>{m.recipient}</td>
                  <td style={styles.td}>{m.subject || (m.body || '').slice(0, 60)}</td>
                  <td style={styles.td}>
                    <Badge kind={(m.status || 'queued').toUpperCase()}>{m.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div style={styles.kpi}>
      <div style={styles.kpiLabel}>{label}</div>
      <div style={styles.kpiValue}>{value}</div>
      {sub && <div style={styles.kpiSub}>{sub}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tab 2: Campaigns
// ────────────────────────────────────────────────────────────────────
function CampaignsTab({ setActiveTab }) {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(API + '/marketing/messages?page=' + page + '&limit=' + limit)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('messages ' + r.status)))
      .then(d => { if (alive) { setData(d); setError(null); } })
      .catch(err => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [page]);

  const messages = data && Array.isArray(data.messages) ? data.messages : [];
  const total = data ? data.total : 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ ...styles.cardTitle, marginBottom: 0 }}>Campaign history</div>
        <button style={styles.primaryBtn} onClick={() => setActiveTab('Campaign builder')}>+ New campaign</button>
      </div>

      {error && <div style={styles.errorBox}>Failed to load: {error}</div>}
      {loading && <div style={styles.emptyState}>Loading…</div>}

      {!loading && !error && messages.length === 0 && (
        <div style={styles.emptyState}>
          <div style={{ marginBottom: 12 }}>No campaigns sent yet</div>
          <button style={styles.primaryBtn} onClick={() => setActiveTab('Campaign builder')}>Create your first campaign</button>
        </div>
      )}

      {messages.length > 0 && (
        <>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Recipient</th>
                <th style={styles.th}>Channel</th>
                <th style={styles.th}>Subject / Preview</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Sent</th>
              </tr>
            </thead>
            <tbody>
              {messages.map(m => (
                <tr key={m.id}>
                  <td style={styles.td}>{m.recipient}</td>
                  <td style={styles.td}><Badge kind={m.channel === 'sms' ? 'SMS' : 'EMAIL'}>{m.channel}</Badge></td>
                  <td style={styles.td}>{m.subject || (m.body || '').slice(0, 80)}</td>
                  <td style={styles.td}>
                    <Badge kind={(m.status || 'queued').toUpperCase()}>{m.status}</Badge>
                    {m.error_message && (
                      <div style={{ fontSize: 10, color: '#E84545', marginTop: 4 }}>{m.error_message}</div>
                    )}
                  </td>
                  <td style={styles.td}>{formatShortDate(m.sent_at || m.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
            <div style={{ fontSize: 11, color: MUTED }}>
              Page {page} of {totalPages} — {total} total
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={page <= 1 ? styles.disabledBtn : styles.ghostBtn} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>← Prev</button>
              <button style={page >= totalPages ? styles.disabledBtn : styles.ghostBtn} disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next →</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tab 3: Campaign builder
// ────────────────────────────────────────────────────────────────────
const GOALS = [
  'Announce new cigar arrival',
  'Re-engage inactive member',
  'Promote event or brand night',
  'Credit expiry reminder',
  'Convert guest to member',
  'New member welcome',
  'Custom',
];
const CHANNELS = ['Email', 'SMS'];
const TONES = ['Warm & personal', 'Exclusive & elevated', 'Urgent & direct', 'Casual & friendly'];

function BuilderTab({ onSent }) {
  const [goal, setGoal] = useState(GOALS[0]);
  const [customGoal, setCustomGoal] = useState('');
  const [channel, setChannel] = useState('Email');
  const [audience, setAudience] = useState(ALL_AUDIENCE);
  const [context, setContext] = useState('');
  const [tone, setTone] = useState(TONES[0]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [sendState, setSendState] = useState(null);

  const buildPrompt = useCallback(() => {
    const ch = channel.toUpperCase() === 'SMS' ? 'SMS' : 'Email';
    const effectiveGoal = goal === 'Custom'
      ? (customGoal.trim() || 'Custom goal (no description provided)')
      : goal;
    return [
      `You are writing marketing copy for ${LOUNGE_NAME}, a premium cigar lounge.`,
      '',
      'Campaign goal: ' + effectiveGoal,
      'Channel: ' + ch,
      'Audience: ' + audience,
      'Context: ' + (context || 'No additional context provided.'),
      'Tone: ' + tone,
      '',
      'Write a ' + (ch === 'SMS' ? 'SMS message under 160 characters' : 'marketing email with subject line and body') + '.',
      'For email: Start with "Subject: [subject line]" then blank line then body.',
      'Keep it premium, authentic, personal — not corporate.',
      'Sign off from the ownership team.',
      'Write ONLY the message content, no preamble.',
    ].join('\n');
  }, [goal, customGoal, channel, audience, context, tone]);

  async function generate() {
    setLoading(true);
    setError(null);
    setSendState(null);
    try {
      const raw = await callAI(buildPrompt());
      const ch = channel.toUpperCase() === 'SMS' ? 'SMS' : 'Email';
      if (ch === 'SMS') {
        setDraft({ subject: '', body: raw.trim(), channel: 'SMS', tone, raw });
      } else {
        const parsed = parseEmailDraft(raw);
        setDraft({ subject: parsed.subject, body: parsed.body, channel: 'Email', tone, raw });
      }
    } catch (err) {
      console.error('[Marketing] AI draft error', err);
      setError('Draft generation failed — try again');
    } finally {
      setLoading(false);
    }
  }

  async function sendNow() {
    if (!draft) return;
    const ok = window.confirm('Send this ' + draft.channel + ' to ' + audience + '? This cannot be undone.');
    if (!ok) return;
    setSendState({ kind: 'pending', message: 'Sending…' });
    try {
      const res = await sendCampaign({
        channel: draft.channel === 'SMS' ? 'SMS' : 'EMAIL',
        audience,
        body: draft.body,
        subject: draft.subject,
      });
      const msg = 'Sent ' + (res.sent || 0) + ' of ' + (res.total || 0) + ' (failed: ' + (res.failed || 0) + ')';
      setSendState({ kind: (res.failed || 0) > 0 ? 'partial' : 'ok', message: msg });
      if (typeof onSent === 'function') onSent();
    } catch (err) {
      setSendState({ kind: 'error', message: err.message || 'Send failed' });
    }
  }

  function copy() {
    if (!draft) return;
    const text = draft.subject ? ('Subject: ' + draft.subject + '\n\n' + draft.body) : draft.body;
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
  }

  return (
    <div style={styles.twoCol}>
      <div style={styles.card}>
        <div style={styles.cardTitle}>Campaign setup</div>

        <div style={styles.field}>
          <label style={styles.label}>Goal</label>
          <select style={styles.select} value={goal} onChange={e => setGoal(e.target.value)}>
            {GOALS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          {goal === 'Custom' && (
            <input
              style={{ ...styles.input, marginTop: 6 }}
              value={customGoal}
              onChange={e => setCustomGoal(e.target.value)}
              placeholder="Type your campaign goal — e.g. Drive RSVPs for whiskey pairing dinner"
            />
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Channel</label>
          <select style={styles.select} value={channel} onChange={e => setChannel(e.target.value)}>
            {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Audience</label>
          <select style={styles.select} value={audience} onChange={e => setAudience(e.target.value)}>
            {AUDIENCES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 6, lineHeight: 1.4 }}>
            🔒 Each recipient receives an individual email — addresses are never shared between recipients.
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Context / details</label>
          <textarea
            style={styles.textarea}
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder="e.g. Padrón 1926 Serie just arrived, limited quantity, $28/stick"
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Tone</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TONES.map(t => (
              <button key={t} style={styles.chip(tone === t)} onClick={() => setTone(t)}>{t}</button>
            ))}
          </div>
        </div>

        <button
          style={loading ? { ...styles.primaryBtnFull, opacity: 0.6, cursor: 'wait' } : styles.primaryBtnFull}
          onClick={generate}
          disabled={loading}
        >
          {loading ? 'Crafting your message…' : 'Generate AI draft'}
        </button>
        {error && <div style={styles.errorBox}>{error}</div>}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>Draft</div>
        {!draft && !loading && (
          <div style={styles.emptyState}>Configure your campaign and generate a draft</div>
        )}
        {loading && <div style={styles.emptyState}>Crafting your message…</div>}
        {draft && !loading && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <Badge kind={draft.channel === 'SMS' ? 'SMS' : 'EMAIL'}>{draft.channel}</Badge>
              <span style={{ fontSize: 11, color: MUTED }}>Tone: {draft.tone}</span>
            </div>
            {draft.subject && (
              <div style={{ marginBottom: 10 }}>
                <div style={styles.label}>Subject</div>
                <div style={{ fontSize: 14, color: TEXT, fontWeight: 600, marginTop: 4 }}>{draft.subject}</div>
              </div>
            )}
            <div style={{ background: INNER, border: '1px solid ' + BORDER, borderRadius: 6, padding: 12, color: TEXT, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {draft.body}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <button style={styles.primaryBtn} onClick={sendNow}>Send now</button>
              <button style={styles.ghostBtn} onClick={generate}>Regenerate</button>
              <button style={styles.ghostBtn} onClick={copy}>Copy</button>
            </div>
            {sendState && (
              <div style={
                sendState.kind === 'ok' ? styles.successBox
                : sendState.kind === 'pending' ? { ...styles.successBox, background: '#0A1A2E', color: '#85B7EB', border: '1px solid #1E2A3D' }
                : sendState.kind === 'partial' ? { ...styles.successBox, background: '#3D1A00', color: '#EF9F27', border: '1px solid #4D2A0A' }
                : styles.errorBox
              }>
                {sendState.message}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tab 4: Social content
// ────────────────────────────────────────────────────────────────────
const TOPICS = [
  'New cigar arrival',
  'Upcoming event or brand night',
  'Why join us',
  'Cigar of the week spotlight',
  'Behind the scenes',
  'Member milestone',
  'Custom',
];
const PLATFORMS = ['Instagram', 'Facebook', 'LinkedIn'];

function SocialTab() {
  const [topic, setTopic] = useState(TOPICS[0]);
  const [customTopic, setCustomTopic] = useState('');
  const [context, setContext] = useState('');
  const [selected, setSelected] = useState({ Instagram: true, Facebook: true, LinkedIn: false });
  const [posts, setPosts] = useState({}); // platform -> { text } | { error } | { loading: true }
  const [loading, setLoading] = useState(false);

  function togglePlatform(p) {
    setSelected(s => ({ ...s, [p]: !s[p] }));
  }

  function platformPrompt(platform) {
    const effectiveTopic = topic === 'Custom'
      ? (customTopic.trim() || 'Custom topic (no description provided)')
      : topic;
    return [
      `You are writing social media content for ${LOUNGE_NAME}, a premium cigar lounge.`,
      '',
      'Topic: ' + effectiveTopic,
      'Context: ' + (context || 'No additional context provided.'),
      'Platform: ' + platform,
      '',
      'Write a social media post for ' + platform + '.',
      '- Instagram: 150-200 words, sensory/atmospheric, 5-8 hashtags at end',
      '- Facebook: 100-150 words, community-focused, warmer tone',
      '- LinkedIn: 80-120 words, professional, business/membership angle',
      '',
      'Voice: premium, personal, passionate about cigars and hospitality.',
      'Write ONLY the post, no preamble.',
    ].join('\n');
  }

  // Generate one platform with isolated try/catch — a failed platform
  // never blocks others, and the result lands in state independently
  // so each card flips from loading → text or error on its own.
  async function generateOne(p) {
    setPosts(prev => ({ ...prev, [p]: { loading: true } }));
    try {
      const text = await callAI(platformPrompt(p));
      setPosts(prev => ({ ...prev, [p]: { text } }));
    } catch (err) {
      setPosts(prev => ({ ...prev, [p]: { error: (err && err.message) || 'Failed' } }));
    }
  }

  async function generate() {
    const chosen = PLATFORMS.filter(p => selected[p]);
    if (chosen.length === 0) return;
    setLoading(true);
    setPosts({});
    // Promise.all on isolated calls — each settles independently.
    await Promise.all(chosen.map(p => generateOne(p)));
    setLoading(false);
  }

  return (
    <div>
      <div style={{ ...styles.card, marginBottom: 16 }}>
        <div style={styles.cardTitle}>{`Generate platform-specific content in ${LOUNGE_NAME}'s voice`}</div>

        <div style={styles.field}>
          <label style={styles.label}>Topic</label>
          <select style={styles.select} value={topic} onChange={e => setTopic(e.target.value)}>
            {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {topic === 'Custom' && (
            <input
              style={{ ...styles.input, marginTop: 6 }}
              value={customTopic}
              onChange={e => setCustomTopic(e.target.value)}
              placeholder="Type your topic — e.g. Father's Day cigar gift bundle"
            />
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Context</label>
          <textarea
            style={styles.textarea}
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder="e.g. Davidoff brand night May 3rd, limited seats, 6-cigar flight"
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Platforms</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PLATFORMS.map(p => (
              <button key={p} style={styles.chip(!!selected[p])} onClick={() => togglePlatform(p)}>{p}</button>
            ))}
          </div>
        </div>

        <button
          style={loading ? { ...styles.primaryBtnFull, opacity: 0.6, cursor: 'wait' } : styles.primaryBtnFull}
          onClick={generate}
          disabled={loading}
        >
          {loading ? 'Generating posts…' : 'Generate posts'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 12 }}>
        {PLATFORMS.filter(p => selected[p]).map(p => {
          const post = posts[p];
          const isLoading = post && post.loading;
          const isError = post && post.error;
          const isText = post && post.text;
          return (
            <div key={p} style={styles.card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ ...styles.cardTitle, marginBottom: 0 }}>{p}</div>
                <Badge kind="EMAIL">{p}</Badge>
              </div>
              {!post && !loading && <div style={styles.emptyState}>Click Generate posts</div>}
              {isLoading && <div style={styles.emptyState}>Writing…</div>}
              {isError && (
                <>
                  <div style={styles.errorBox}>{post.error}</div>
                  <div style={{ marginTop: 10 }}>
                    <button style={styles.ghostBtn} onClick={() => generateOne(p)}>Retry</button>
                  </div>
                </>
              )}
              {isText && (
                <>
                  <div style={{ background: INNER, border: '1px solid ' + BORDER, borderRadius: 6, padding: 12, color: TEXT, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {post.text}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <button style={styles.ghostBtn} onClick={() => navigator.clipboard && navigator.clipboard.writeText(post.text)}>Copy</button>
                    <button style={styles.ghostBtn} onClick={() => generateOne(p)}>Regenerate</button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tab 5: Outreach center
// ────────────────────────────────────────────────────────────────────
// Privacy note shown anywhere a multi-recipient send can fire.
const BCC_NOTE = '🔒 Each recipient receives an individual email — addresses are never shared between recipients.';

const OUTREACH_GROUPS = [
  'All members',
  'Charter members',
  'Founding members',
  'Reserve members',
  'Corporate members',
];

// Map a group label to a predicate over a member row. Tier filters
// match against tier_name / tier (case-insensitive substring) so
// minor naming drift in the DB doesn't break the filter.
function groupPredicate(group) {
  if (group === 'All members') return () => true;
  if (group === 'Charter members') return m => /charter/i.test(String(m.tier_name || m.tier || ''));
  if (group === 'Founding members') return m => /founding/i.test(String(m.tier_name || m.tier || ''));
  if (group === 'Reserve members') return m => /reserve/i.test(String(m.tier_name || m.tier || ''));
  if (group === 'Corporate members') return m => /corporate/i.test(String(m.tier_name || m.tier || ''));
  return () => false;
}

function OutreachTab({ members, churnRisk, lowCredit, loadingMembers, onSent }) {
  const [drafts, setDrafts] = useState({});

  const candidates = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const m of churnRisk) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      const last = m.last_visit || m.created_at || m.join_date;
      const d = daysAgo(last);
      out.push({ ...m, _reason: d == null ? 'No visit on record' : ('Inactive ' + d + ' days'), _kind: 'churn' });
    }
    for (const m of lowCredit) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push({ ...m, _reason: 'Credit balance: $' + (parseFloat(m.credit_balance || 0).toFixed(2)), _kind: 'credit' });
    }
    return out;
  }, [churnRisk, lowCredit]);

  function buildPrompt(m) {
    return [
      'Write a personalized re-engagement email for a premium cigar lounge member.',
      '',
      'Member name: ' + (m.name || 'Member'),
      'Membership tier: ' + (m.tier_name || m.tier || 'Member'),
      'Credit balance: $' + (parseFloat(m.credit_balance || 0).toFixed(2)),
      'Member since: ' + (m.created_at || m.join_date || 'unknown'),
      '',
      'Write a warm, personal email from the ownership team to re-engage this member.',
      'If credit is unused, mention it gently. Keep it genuine — not a marketing blast.',
      'Start with "Subject: [subject]" then blank line then body.',
      `Sign as the team, ${LOUNGE_NAME}.`,
      'Write ONLY the email, no preamble.',
    ].join('\n');
  }

  async function generateFor(m) {
    setDrafts(d => ({ ...d, [m.id]: { ...(d[m.id] || {}), loading: true, error: null, sendState: null } }));
    try {
      const raw = await callAI(buildPrompt(m));
      const parsed = parseEmailDraft(raw);
      setDrafts(d => ({ ...d, [m.id]: { loading: false, raw, subject: parsed.subject, body: parsed.body } }));
    } catch (err) {
      setDrafts(d => ({ ...d, [m.id]: { ...(d[m.id] || {}), loading: false, error: err.message || 'Failed' } }));
    }
  }

  async function sendFor(m) {
    const d = drafts[m.id];
    if (!d || !d.body) return;
    if (!m.email) {
      setDrafts(s => ({ ...s, [m.id]: { ...d, sendState: { kind: 'error', message: 'No email on file for this member' } } }));
      return;
    }
    const ok = window.confirm('Send this email to ' + m.name + ' <' + m.email + '>? This cannot be undone.');
    if (!ok) return;
    setDrafts(s => ({ ...s, [m.id]: { ...d, sendState: { kind: 'pending', message: 'Sending…' } } }));
    try {
      const res = await fetch(API + '/marketing/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: m.email,
          subject: d.subject || `A note from ${LOUNGE_NAME}`,
          html: d.body,
          memberId: m.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || ('HTTP ' + res.status));
      setDrafts(s => ({ ...s, [m.id]: { ...d, sendState: { kind: 'ok', message: 'Sent — id ' + (data.messageId || '—') } } }));
      if (typeof onSent === 'function') onSent();
    } catch (err) {
      setDrafts(s => ({ ...s, [m.id]: { ...d, sendState: { kind: 'error', message: err.message || 'Send failed' } } }));
    }
  }

  function dismiss(m) {
    setDrafts(s => {
      const next = { ...s };
      delete next[m.id];
      return next;
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ManualSelectSection members={members} loadingMembers={loadingMembers} onSent={onSent} />
      <GroupSendSection members={members} loadingMembers={loadingMembers} onSent={onSent} />

      <div style={styles.card}>
      <div style={styles.cardTitle}>AI-detected outreach candidates</div>

      {loadingMembers && <div style={styles.emptyState}>Loading members…</div>}
      {!loadingMembers && candidates.length === 0 && (
        <div style={styles.emptyState}>All members are active and engaged!</div>
      )}

      {candidates.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {candidates.map(m => {
            const d = drafts[m.id];
            const dotColor = m._kind === 'churn' ? '#E84545' : '#EF9F27';
            return (
              <div key={m.id} style={{ background: INNER, border: '1px solid ' + BORDER, borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>
                      <Dot color={dotColor} />{m.name || 'Member #' + m.id}
                      {(m.tier_name || m.tier) && (
                        <span style={{ marginLeft: 10 }}><Badge kind="ACTIVE">{m.tier_name || m.tier}</Badge></span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, marginLeft: 16, marginTop: 4 }}>
                      {m._reason}{m.email ? ' • ' + m.email : ' • no email on file'}
                    </div>
                  </div>
                  <button
                    style={d && d.loading ? { ...styles.ghostBtn, opacity: 0.6, cursor: 'wait' } : styles.ghostBtn}
                    onClick={() => generateFor(m)}
                    disabled={d && d.loading}
                  >
                    {d && d.loading ? 'Crafting…' : (d && d.body ? 'Regenerate' : 'Generate draft')}
                  </button>
                </div>

                {d && d.error && <div style={styles.errorBox}>{d.error}</div>}

                {d && d.body && (
                  <div style={{ marginTop: 10 }}>
                    {d.subject && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={styles.label}>Subject</div>
                        <div style={{ fontSize: 13, color: TEXT, fontWeight: 600, marginTop: 4 }}>{d.subject}</div>
                      </div>
                    )}
                    <div style={{ background: BG, border: '1px solid ' + BORDER, borderRadius: 6, padding: 12, color: TEXT, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {d.body}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      <button style={styles.primaryBtn} onClick={() => sendFor(m)}>Send email</button>
                      <button style={styles.ghostBtn} onClick={() => generateFor(m)}>Regenerate</button>
                      <button style={styles.ghostBtn} onClick={() => dismiss(m)}>Dismiss</button>
                    </div>
                    {d.sendState && (
                      <div style={
                        d.sendState.kind === 'ok' ? styles.successBox
                        : d.sendState.kind === 'pending' ? { ...styles.successBox, background: '#0A1A2E', color: '#85B7EB', border: '1px solid #1E2A3D' }
                        : styles.errorBox
                      }>
                        {d.sendState.message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Outreach: Section A — manual member selection
// ────────────────────────────────────────────────────────────────────

function ManualSelectSection({ members, loadingMembers, onSent }) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState([]);
  const [draft, setDraft] = useState(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return members
      .filter(m => (m.name || '').toLowerCase().includes(q))
      .slice(0, 12);
  }, [members, query]);

  const pickedRows = useMemo(() => {
    const ids = new Set(picked);
    return members.filter(m => ids.has(m.id));
  }, [members, picked]);

  function toggle(id) {
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  }

  function buildPrompt() {
    if (pickedRows.length === 1) {
      const m = pickedRows[0];
      return [
        'Write a personalized re-engagement email for a premium cigar lounge member.',
        '',
        'Member name: ' + (m.name || 'Member'),
        'Membership tier: ' + (m.tier_name || m.tier || 'Member'),
        'Credit balance: $' + (parseFloat(m.credit_balance || 0).toFixed(2)),
        '',
        'Write a warm, personal email from the ownership team.',
        'If credit is unused, mention it gently. Keep it genuine — not a marketing blast.',
        'Start with "Subject: [subject]" then blank line then body.',
        `Sign as the team, ${LOUNGE_NAME}.`,
        'Write ONLY the email, no preamble.',
      ].join('\n');
    }
    const tiers = [...new Set(pickedRows.map(m => m.tier_name || m.tier).filter(Boolean))];
    return [
      `Write a group-appropriate marketing email from ${LOUNGE_NAME} to a hand-picked set of premium cigar lounge members.`,
      '',
      'Recipients: ' + pickedRows.length + ' members',
      tiers.length ? ('Tiers represented: ' + tiers.join(', ')) : '',
      '',
      'The email must NOT name any individual. It must read warmly to each recipient as if written for them — addresses are never shared between recipients.',
      'Voice: premium, personal, from the ownership team.',
      'Start with "Subject: [subject]" then blank line then body.',
      `Sign as the team, ${LOUNGE_NAME}.`,
      'Write ONLY the email, no preamble.',
    ].filter(Boolean).join('\n');
  }

  async function generate() {
    if (pickedRows.length === 0) return;
    setDraft({ loading: true });
    try {
      const raw = await callAI(buildPrompt());
      const parsed = parseEmailDraft(raw);
      setDraft({ subject: parsed.subject, body: parsed.body, raw });
    } catch (err) {
      setDraft({ error: (err && err.message) || 'Failed' });
    }
  }

  async function sendNow() {
    if (!draft || !draft.body) return;
    const recipients = pickedRows.filter(m => m.email);
    const skipped = pickedRows.length - recipients.length;
    if (recipients.length === 0) {
      setDraft(d => ({ ...d, sendState: { kind: 'error', message: 'No recipients with email addresses on file.' } }));
      return;
    }
    const ok = window.confirm('Send this email to ' + recipients.length + ' member(s)? This cannot be undone.');
    if (!ok) return;
    setDraft(d => ({ ...d, sendState: { kind: 'pending', message: 'Sending ' + recipients.length + ' email(s)…' } }));
    let sent = 0; let failed = 0;
    for (const m of recipients) {
      try {
        const res = await fetch(API + '/marketing/email/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: m.email,
            subject: draft.subject || `A note from ${LOUNGE_NAME}`,
            html: draft.body,
            memberId: m.id,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) sent += 1; else failed += 1;
      } catch (e) { failed += 1; }
    }
    const summary = 'Sent ' + sent + ' / failed ' + failed + (skipped ? (' / skipped ' + skipped + ' (no email)') : '');
    setDraft(d => ({ ...d, sendState: { kind: failed > 0 ? 'partial' : 'ok', message: summary } }));
    if (typeof onSent === 'function') onSent();
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>Send to specific members</div>

      {loadingMembers && <div style={styles.emptyState}>Loading members…</div>}

      {!loadingMembers && (
        <>
          <div style={styles.field}>
            <label style={styles.label}>Search members by name</label>
            <div style={{ position: 'relative' }}>
              <input
                style={{ ...styles.input, paddingRight: 32, width: '100%', boxSizing: 'border-box' }}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type a name to search…"
              />
              {query && (
                <span
                  onClick={() => setQuery('')}
                  style={{
                    position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
                    cursor: 'pointer', color: GOLD, fontSize: 18, padding: 8, lineHeight: 1,
                  }}
                  aria-label="Clear search"
                >×</span>
              )}
            </div>
            {matches.length > 0 && (
              <div style={{ marginTop: 6, background: INNER, border: '1px solid ' + BORDER, borderRadius: 6, padding: 6, maxHeight: 200, overflowY: 'auto' }}>
                {matches.map(m => {
                  const isPicked = picked.includes(m.id);
                  return (
                    <div
                      key={m.id}
                      onClick={() => toggle(m.id)}
                      style={{
                        padding: '6px 8px', cursor: 'pointer', borderRadius: 4,
                        background: isPicked ? GOLD_DIM : 'transparent',
                        color: isPicked ? GOLD : TEXT,
                        fontSize: 13, fontWeight: isPicked ? 600 : 400,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}
                    >
                      <span>{m.name}{(m.tier_name || m.tier) ? ' • ' + (m.tier_name || m.tier) : ''}</span>
                      <span style={{ fontSize: 10, color: MUTED }}>{m.email || 'no email'}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {pickedRows.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={styles.label}>Selected ({pickedRows.length})</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {pickedRows.map(m => (
                  <span key={m.id} style={{ ...styles.chip(true), display: 'inline-flex', gap: 6 }}>
                    {m.name}
                    <span onClick={() => toggle(m.id)} style={{ cursor: 'pointer' }}>×</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {pickedRows.length > 1 && (
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 10, lineHeight: 1.4 }}>
              {BCC_NOTE}
            </div>
          )}

          <button
            style={pickedRows.length === 0 ? styles.disabledBtn : styles.primaryBtn}
            disabled={pickedRows.length === 0 || (draft && draft.loading)}
            onClick={generate}
          >
            {draft && draft.loading ? 'Crafting…' : 'Generate & review'}
          </button>

          {draft && draft.error && <div style={styles.errorBox}>{draft.error}</div>}

          {draft && draft.body && (
            <div style={{ marginTop: 14 }}>
              {draft.subject && (
                <div style={{ marginBottom: 8 }}>
                  <div style={styles.label}>Subject</div>
                  <div style={{ fontSize: 13, color: TEXT, fontWeight: 600, marginTop: 4 }}>{draft.subject}</div>
                </div>
              )}
              <div style={{ background: INNER, border: '1px solid ' + BORDER, borderRadius: 6, padding: 12, color: TEXT, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {draft.body}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button style={styles.primaryBtn} onClick={sendNow}>Send email</button>
                <button style={styles.ghostBtn} onClick={generate}>Regenerate</button>
              </div>
              {draft.sendState && (
                <div style={
                  draft.sendState.kind === 'ok' ? styles.successBox
                  : draft.sendState.kind === 'pending' ? { ...styles.successBox, background: '#0A1A2E', color: '#85B7EB', border: '1px solid #1E2A3D' }
                  : draft.sendState.kind === 'partial' ? { ...styles.successBox, background: '#3D1A00', color: '#EF9F27', border: '1px solid #4D2A0A' }
                  : styles.errorBox
                }>
                  {draft.sendState.message}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Outreach: Section B — group send
// ────────────────────────────────────────────────────────────────────

function GroupSendSection({ members, loadingMembers, onSent }) {
  const [group, setGroup] = useState(OUTREACH_GROUPS[0]);
  const [draft, setDraft] = useState(null);

  const groupRows = useMemo(() => members.filter(groupPredicate(group)), [members, group]);
  const recipients = useMemo(() => groupRows.filter(m => m.email), [groupRows]);

  function buildPrompt() {
    return [
      `Write a marketing email from ${LOUNGE_NAME} to a member group.`,
      '',
      'Group: ' + group,
      'Recipient count: ' + groupRows.length,
      '',
      'The email must read warmly to each recipient as if written for them — addresses are never shared between recipients.',
      'Voice: premium, personal, passionate about cigars and hospitality, from the ownership team.',
      'Start with "Subject: [subject]" then blank line then body.',
      `Sign as the team, ${LOUNGE_NAME}.`,
      'Write ONLY the email, no preamble.',
    ].join('\n');
  }

  async function generate() {
    setDraft({ loading: true });
    try {
      const raw = await callAI(buildPrompt());
      const parsed = parseEmailDraft(raw);
      setDraft({ subject: parsed.subject, body: parsed.body, raw });
    } catch (err) {
      setDraft({ error: (err && err.message) || 'Failed' });
    }
  }

  async function sendNow() {
    if (!draft || !draft.body) return;
    if (recipients.length === 0) {
      setDraft(d => ({ ...d, sendState: { kind: 'error', message: 'No recipients with email in this group.' } }));
      return;
    }
    const ok = window.confirm('Send this email to ' + recipients.length + ' ' + group.toLowerCase() + '? This cannot be undone.');
    if (!ok) return;
    setDraft(d => ({ ...d, sendState: { kind: 'pending', message: 'Sending ' + recipients.length + ' email(s)…' } }));
    let sent = 0; let failed = 0;
    for (const m of recipients) {
      try {
        const res = await fetch(API + '/marketing/email/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: m.email,
            subject: draft.subject || `A note from ${LOUNGE_NAME}`,
            html: draft.body,
            memberId: m.id,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) sent += 1; else failed += 1;
      } catch (e) { failed += 1; }
    }
    const skipped = groupRows.length - recipients.length;
    const summary = 'Sent ' + sent + ' / failed ' + failed + (skipped ? (' / skipped ' + skipped + ' (no email)') : '');
    setDraft(d => ({ ...d, sendState: { kind: failed > 0 ? 'partial' : 'ok', message: summary } }));
    if (typeof onSent === 'function') onSent();
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>Send to group</div>

      {loadingMembers && <div style={styles.emptyState}>Loading members…</div>}

      {!loadingMembers && (
        <>
          <div style={styles.field}>
            <label style={styles.label}>Group</label>
            <select style={styles.select} value={group} onChange={e => { setGroup(e.target.value); setDraft(null); }}>
              {OUTREACH_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>
              {groupRows.length} match{groupRows.length === 1 ? '' : 'es'} • {recipients.length} with email on file
            </div>
          </div>

          <div style={{ fontSize: 11, color: MUTED, marginBottom: 10, lineHeight: 1.4 }}>
            {BCC_NOTE}
          </div>

          <button
            style={recipients.length === 0 ? styles.disabledBtn : styles.primaryBtn}
            disabled={recipients.length === 0 || (draft && draft.loading)}
            onClick={generate}
          >
            {draft && draft.loading ? 'Crafting…' : 'Generate & review'}
          </button>

          {draft && draft.error && <div style={styles.errorBox}>{draft.error}</div>}

          {draft && draft.body && (
            <div style={{ marginTop: 14 }}>
              {draft.subject && (
                <div style={{ marginBottom: 8 }}>
                  <div style={styles.label}>Subject</div>
                  <div style={{ fontSize: 13, color: TEXT, fontWeight: 600, marginTop: 4 }}>{draft.subject}</div>
                </div>
              )}
              <div style={{ background: INNER, border: '1px solid ' + BORDER, borderRadius: 6, padding: 12, color: TEXT, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {draft.body}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button style={styles.primaryBtn} onClick={sendNow}>Send email</button>
                <button style={styles.ghostBtn} onClick={generate}>Regenerate</button>
              </div>
              {draft.sendState && (
                <div style={
                  draft.sendState.kind === 'ok' ? styles.successBox
                  : draft.sendState.kind === 'pending' ? { ...styles.successBox, background: '#0A1A2E', color: '#85B7EB', border: '1px solid #1E2A3D' }
                  : draft.sendState.kind === 'partial' ? { ...styles.successBox, background: '#3D1A00', color: '#EF9F27', border: '1px solid #4D2A0A' }
                  : styles.errorBox
                }>
                  {draft.sendState.message}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tab 6: Content calendar
// ────────────────────────────────────────────────────────────────────
const CHANNEL_OPTS = ['Email', 'SMS', 'Both'];

function monthLabel(offset) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

const SEED_CURRENT = [
  { date: 'Apr 4', name: 'Welcome series — new Charter member', channel: 'Email', status: 'SENT' },
  { date: 'Apr 12', name: 'Padrón 1926 arrival announcement', channel: 'SMS', status: 'SENT' },
  { date: 'Apr 26', name: 'Weekend lounge hours reminder', channel: 'Email', status: 'SCHEDULED' },
  { date: 'May 3', name: 'Brand night: Davidoff May 3', channel: 'Both', status: 'PLANNED' },
];

const SEED_NEXT = [
  { date: 'May 6', name: 'May humidor refresh', channel: 'Email', status: 'PLANNED' },
  { date: 'May 12', name: 'Mid-month spirits feature', channel: 'Email', status: 'PLANNED' },
  { date: 'May 21', name: 'Credit reset reminder', channel: 'SMS', status: 'AUTO' },
  { date: 'May 28', name: 'Memorial Day weekend hours', channel: 'Email', status: 'PLANNED' },
];

function CalendarTab() {
  const [current, setCurrent] = useState(() => SEED_CURRENT.map((r, i) => ({ id: 'c' + i, ...r })));
  const [next, setNext] = useState(() => SEED_NEXT.map((r, i) => ({ id: 'n' + i, ...r })));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: '', name: '', channel: 'Email', audience: 'All members', when: 'current' });

  function addItem() {
    if (!form.date || !form.name) return;
    const row = { id: 'u' + Date.now(), date: form.date, name: form.name, channel: form.channel, audience: form.audience, status: 'PLANNED' };
    if (form.when === 'next') setNext(rows => [...rows, row]);
    else setCurrent(rows => [...rows, row]);
    setForm({ date: '', name: '', channel: 'Email', audience: 'All members', when: form.when });
    setShowForm(false);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: MUTED }}>
          Calendar items saved locally. Backend persistence coming Phase 1.5.
        </div>
        <button style={styles.primaryBtn} onClick={() => setShowForm(s => !s)}>+ Add to calendar</button>
      </div>

      {showForm && (
        <div style={{ ...styles.card, marginBottom: 14 }}>
          <div style={styles.cardTitle}>New calendar item</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 10 }}>
            <div style={styles.field}>
              <label style={styles.label}>Date</label>
              <input style={styles.input} placeholder="May 12" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Content</label>
              <input style={styles.input} placeholder="Mid-month spirits feature" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Channel</label>
              <select style={styles.select} value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))}>
                {CHANNEL_OPTS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Audience</label>
              <input style={styles.input} value={form.audience} onChange={e => setForm(f => ({ ...f, audience: e.target.value }))} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Month</label>
              <select style={styles.select} value={form.when} onChange={e => setForm(f => ({ ...f, when: e.target.value }))}>
                <option value="current">{monthLabel(0)}</option>
                <option value="next">{monthLabel(1)}</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button style={styles.primaryBtn} onClick={addItem}>Save item</button>
            <button style={styles.ghostBtn} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 10 }}>Saved to this session only.</div>
        </div>
      )}

      <CalendarSection title={monthLabel(0)} rows={current} />
      <div style={{ height: 14 }} />
      <CalendarSection title={monthLabel(1)} rows={next} />
    </div>
  );
}

function CalendarSection({ title, rows }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>{title}</div>
      {rows.length === 0 ? (
        <div style={styles.emptyState}>No items planned.</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Content</th>
              <th style={styles.th}>Channel</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={styles.td}>{r.date}</td>
                <td style={styles.td}>{r.name}</td>
                <td style={styles.td}><Badge kind={(r.channel || 'EMAIL').toUpperCase()}>{r.channel}</Badge></td>
                <td style={styles.td}><Badge kind={(r.status || 'PLANNED').toUpperCase()}>{r.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
